import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { LayoutDashboard, Briefcase, Phone, Bot, Users, LogOut, Mic, ChevronDown, Check, Plus, BarChart3, FileText, Settings, UserCheck, Radar, Activity } from 'lucide-react';import api from '@/lib/api';

const navItems = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/jobs', icon: Briefcase, label: 'Job Roles' },
  { to: '/candidates', icon: UserCheck, label: 'Candidates' },
  { to: '/calls', icon: Phone, label: 'Screenings' },
  { to: '/agents', icon: Bot, label: 'AI Agents' },
  { to: '/sourcing-agents', icon: Radar, label: 'Sourcing Agents' },
  { to: '/analytics', icon: BarChart3, label: 'Analytics' },
  { to: '/reports', icon: FileText, label: 'Reports' },
  { to: '/team', icon: Users, label: 'Team' },
  { to: '/voice', icon: Mic, label: 'Voice Screening' },
];

// Phase 7+ — Owner-only nav items
const ownerNavItems = [
  { to: '/productivity', icon: Activity, label: 'Productivity' },
  { to: '/settings', icon: Settings, label: 'Settings' },
];

export default function Sidebar({ user, onLogout, workspaces, activeWsId, onSwitchWorkspace }) {
  const location = useLocation();
  const [showWsMenu, setShowWsMenu] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');

  const activeWs = workspaces?.find(w => w.workspace_id === activeWsId);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      const { data } = await api.post('/workspaces', { name: newName.trim() });
      onSwitchWorkspace(data.workspace_id);
      setCreating(false);
      setNewName('');
      setShowWsMenu(false);
    } catch (err) { logger.error(err); }
  };

  return (
    <aside
      className="w-[240px] bg-white/85 backdrop-blur-xl border-r border-slate-200/60 flex flex-col h-screen flex-shrink-0 shadow-[0_8px_30px_rgba(15,23,42,0.06)]"
      data-testid="sidebar"
      style={{ fontFamily: 'Georgia, serif' }}
    >
      {/* Logo */}
      <div className="p-5 border-b border-slate-200/70 bg-gradient-to-r from-white to-blue-50/40">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#1e40af] to-[#3b82f6] flex items-center justify-center shadow-lg shadow-blue-500/20">
            <Mic className="w-4 h-4 text-white" />
          </div>
          <span className="text-[22px] font-bold tracking-tight text-slate-800" style={{ fontFamily: 'Georgia, serif' }}>
            Vocala<span className="text-[#2563eb]">.</span>
          </span>
        </div>
      </div>

      {/* Workspace selector */}
      {workspaces && workspaces.length > 0 && (
        <div className="px-3 pt-3 pb-1 relative">
          <button
            data-testid="workspace-selector"
            onClick={() => setShowWsMenu(!showWsMenu)}
            className="w-full flex items-center justify-between px-3 py-3 rounded-xl bg-slate-50/80 border border-slate-200/70 hover:border-blue-300/60 hover:bg-white transition-all text-left shadow-sm"
          >
            <div className="flex-1 min-w-0">
              <p className="text-[10px] text-slate-400 uppercase tracking-[0.18em] font-bold">Workspace</p>
              <p className="text-sm text-slate-700 font-semibold truncate">{activeWs?.name || 'Select'}</p>
            </div>
            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform flex-shrink-0 ${showWsMenu ? 'rotate-180' : ''}`} />
          </button>

          {showWsMenu && (
            <div
              className="absolute left-3 right-3 top-full mt-2 bg-white/95 backdrop-blur-xl border border-slate-200/70 rounded-2xl shadow-2xl z-50 overflow-hidden"
              data-testid="workspace-dropdown"
            >
              {workspaces.map(ws => (
                <button
                  key={ws.workspace_id}
                  data-testid={`ws-option-${ws.workspace_id}`}
                  onClick={() => { onSwitchWorkspace(ws.workspace_id); setShowWsMenu(false); }}
                  className="w-full flex items-center justify-between px-3 py-3 hover:bg-slate-50/80 transition-colors text-left"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-700 truncate">{ws.name}</p>
                    <p className="text-[10px] text-slate-400">{ws.role} · {ws.member_count} member{ws.member_count !== 1 ? 's' : ''}</p>
                  </div>
                  {ws.workspace_id === activeWsId && <Check className="w-4 h-4 text-[#2563eb] flex-shrink-0" />}
                </button>
              ))}
              <div className="border-t border-slate-100">
                {creating ? (
                  <div className="p-2">
                    <input
                      data-testid="new-ws-name-input"
                      value={newName}
                      onChange={e => setNewName(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleCreate()}
                      placeholder="Workspace name"
                      className="w-full bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-700 text-xs focus:border-[#2563eb] focus:ring-1 focus:ring-[#2563eb]/30 outline-none"
                      autoFocus
                    />
                    <div className="flex gap-1 mt-1">
                      <button onClick={handleCreate} className="flex-1 bg-[#2563eb] text-white text-xs py-1 rounded-md">Create</button>
                      <button onClick={() => { setCreating(false); setNewName(''); }} className="flex-1 text-slate-400 text-xs py-1">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <button
                    data-testid="create-workspace-btn"
                    onClick={() => setCreating(true)}
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-[#2563eb] hover:bg-blue-50 transition-colors text-sm"
                  >
                    <Plus className="w-4 h-4" /> New Workspace
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
        <p className="px-3 pb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Workspace</p>
        {navItems.map(item => {
          const isActive = item.to === '/' ? location.pathname === '/' : location.pathname.startsWith(item.to);
          return (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
              className={`flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-semibold transition-all ${
                isActive
                  ? 'bg-gradient-to-r from-[#2563eb] to-[#3b82f6] text-white shadow-lg shadow-blue-500/20'
                  : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50/80'
              }`}
            >
              <item.icon className="w-4 h-4 flex-shrink-0" />
              {item.label}
            </NavLink>
          );
        })}
        {/* Phase 7 — Settings: owner/org_admin only */}
        {['owner', 'org_admin'].includes(user?.workspace_role_raw || user?.workspace_role || '') && (
          <>
            <div className="my-2 mx-3 border-t border-slate-200/70" />
            {ownerNavItems.map(item => {
              const isActive = location.pathname.startsWith(item.to);
              return (
                <NavLink key={item.to} to={item.to}
                  data-testid={`nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
                  className={`flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-semibold transition-all ${isActive ? 'bg-gradient-to-r from-[#2563eb] to-[#3b82f6] text-white shadow-lg shadow-blue-500/20' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50/80'}`}>
                  <item.icon className="w-4 h-4 flex-shrink-0" />
                  {item.label}
                </NavLink>
              );
            })}
          </>
        )}
      </nav>

      {/* User */}
      <div className="p-3 border-t border-slate-200/70 bg-gradient-to-t from-slate-50/80 to-white">
        <div className="flex items-center gap-3 px-3 py-2.5 mb-2 rounded-xl bg-white/70 border border-slate-200/60 shadow-sm">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#2563eb]/15 to-emerald-500/15 border border-[#2563eb]/20 flex items-center justify-center text-sm font-bold text-[#2563eb]">
            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-800 truncate">{user?.name || 'User'}</p>
            <p className="text-[11px] text-slate-400 truncate">{user?.email || ''}</p>
          </div>
        </div>
        <button
          data-testid="logout-btn"
          onClick={onLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-500 hover:text-red-600 hover:bg-red-50 transition-colors"
        >
          <LogOut className="w-4 h-4" />
          Sign Out
        </button>
      </div>
    </aside>
  );
}
